/**
 * CRRIBBON: Ribbon Styling Section
 * 
 * HEX-based ribbon color controls for Deal Cards Components tab.
 * Features:
 * - Base HEX input with color swatch preview
 * - Border HEX input (AUTO mode when blank)
 * - Live preview updates via debouncing
 * - Undo icons to clear overrides
 * - Flashy gradient mapping
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Palette, 
  Undo2, 
  AlertCircle,
  Info,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  normalizeHex,
  isValidHex,
  buildFlashyGradient,
  computeAutoBorderHex,
  type RibbonGradientColors,
} from "@/lib/deals/hexColorUtils";

// ================================================================
// TYPES
// ================================================================

export interface RibbonStylingConfig {
  /** Canonical input: Base HEX color */
  ribbonBaseHex?: string | null;
  /** Border HEX override (null = AUTO) */
  ribbonBorderHex?: string | null;
  /** Derived: gradient start (HSL string) */
  gradientFrom?: string;
  /** Derived: gradient end (HSL string) */
  gradientTo?: string;
  /** Derived: border color (HSLA string) */
  borderColor?: string;
}

interface RibbonStylingSectionProps {
  /** Current styling config */
  styling: RibbonStylingConfig;
  /** Called when styling changes (debounced from internal state) */
  onChange: (update: Partial<RibbonStylingConfig>) => void;
  /** Called to clear all styling overrides (Undo base) */
  onClearBase: () => void;
  /** Called to clear border override only (Undo border → AUTO) */
  onClearBorder: () => void;
  /** Whether there's a persisted base hex */
  hasBaseOverride: boolean;
  /** Whether there's a persisted border hex */
  hasBorderOverride: boolean;
  /** Deal type for default ribbon color reference */
  dealTypeKey?: string;
}

// Debounce delay for live preview
const DEBOUNCE_MS = 200;

// ================================================================
// HELPERS
// ================================================================

function ColorSwatch({ 
  hex, 
  label,
  isAuto = false,
  className,
}: { 
  hex: string | null | undefined;
  label?: string;
  isAuto?: boolean;
  className?: string;
}) {
  const displayHex = hex || "#808080";
  const isValid = hex ? isValidHex(hex) : false;
  
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div 
        className={cn(
          "w-6 h-6 rounded border-2 shrink-0 transition-colors",
          isValid ? "border-border" : "border-destructive/50",
          !hex && "border-dashed border-muted-foreground/30"
        )}
        style={{ 
          backgroundColor: isValid ? displayHex : "#f0f0f0",
          opacity: isAuto ? 0.7 : 1,
        }}
      />
      {label && (
        <span className="text-[10px] text-muted-foreground">{label}</span>
      )}
    </div>
  );
}

function GradientPreview({ colors }: { colors: RibbonGradientColors | null }) {
  if (!colors) return null;
  
  // Convert HSL strings to CSS
  const fromCss = colors.gradientFrom ? `hsl(${colors.gradientFrom})` : '#333';
  const toCss = colors.gradientTo ? `hsl(${colors.gradientTo})` : '#666';
  const borderCss = colors.borderColor ? `hsla(${colors.borderColor.replace(' / ', ', ')})` : 'transparent';
  
  return (
    <div className="space-y-1">
      <Label className="text-[10px] text-muted-foreground">Gradient Preview</Label>
      <div 
        className="h-8 rounded-md border"
        style={{
          background: `linear-gradient(to right, ${fromCss}, ${toCss})`,
          borderColor: borderCss,
          borderWidth: '2px',
        }}
      />
    </div>
  );
}

// ================================================================
// COMPONENT
// ================================================================

export function RibbonStylingSection({
  styling,
  onChange,
  onClearBase,
  onClearBorder,
  hasBaseOverride,
  hasBorderOverride,
  dealTypeKey = "BONUS",
}: RibbonStylingSectionProps) {
  // Local state for debouncing
  const [localBaseHex, setLocalBaseHex] = useState(styling.ribbonBaseHex ?? "");
  const [localBorderHex, setLocalBorderHex] = useState(styling.ribbonBorderHex ?? "");
  const [baseError, setBaseError] = useState<string | null>(null);
  const [borderError, setBorderError] = useState<string | null>(null);
  
  // Sync external styling to local state
  useEffect(() => {
    setLocalBaseHex(styling.ribbonBaseHex ?? "");
    setLocalBorderHex(styling.ribbonBorderHex ?? "");
  }, [styling.ribbonBaseHex, styling.ribbonBorderHex]);
  
  // Validate and compute gradients
  const baseHexNormalized = useMemo(() => {
    if (!localBaseHex) return null;
    return normalizeHex(localBaseHex);
  }, [localBaseHex]);
  
  const borderHexNormalized = useMemo(() => {
    if (!localBorderHex) return null;
    return normalizeHex(localBorderHex);
  }, [localBorderHex]);
  
  // Compute AUTO border for preview
  const autoBorderHex = useMemo(() => {
    if (!baseHexNormalized) return null;
    return computeAutoBorderHex(baseHexNormalized);
  }, [baseHexNormalized]);
  
  // Compute gradient colors for preview
  const gradientColors = useMemo(() => {
    if (!baseHexNormalized) return null;
    return buildFlashyGradient(baseHexNormalized, borderHexNormalized);
  }, [baseHexNormalized, borderHexNormalized]);
  
  // Debounced commit to parent
  useEffect(() => {
    const timer = setTimeout(() => {
      // Validate base hex
      if (localBaseHex && !baseHexNormalized) {
        setBaseError("Invalid HEX format. Use #RGB or #RRGGBB");
        return;
      }
      setBaseError(null);
      
      // Validate border hex (only if provided)
      if (localBorderHex && !borderHexNormalized) {
        setBorderError("Invalid HEX format. Use #RGB or #RRGGBB");
        return;
      }
      setBorderError(null);
      
      // Build update object
      if (baseHexNormalized && gradientColors) {
        onChange({
          ribbonBaseHex: baseHexNormalized,
          ribbonBorderHex: borderHexNormalized,
          gradientFrom: gradientColors.gradientFrom,
          gradientTo: gradientColors.gradientTo,
          borderColor: gradientColors.borderColor,
        });
      } else if (!localBaseHex) {
        // Clearing base - no partial update needed, handled by onClearBase
      }
    }, DEBOUNCE_MS);
    
    return () => clearTimeout(timer);
  }, [localBaseHex, localBorderHex, baseHexNormalized, borderHexNormalized, gradientColors, onChange]);
  
  // Handle base hex input
  const handleBaseChange = useCallback((value: string) => {
    // Allow any input, validate on debounce
    setLocalBaseHex(value);
    if (value && !isValidHex(value)) {
      setBaseError("Enter valid HEX (e.g., #D3AF37)");
    } else {
      setBaseError(null);
    }
  }, []);
  
  // Handle border hex input
  const handleBorderChange = useCallback((value: string) => {
    setLocalBorderHex(value);
    if (value && !isValidHex(value)) {
      setBorderError("Enter valid HEX");
    } else {
      setBorderError(null);
    }
  }, []);
  
  // Undo handlers
  const handleUndoBase = useCallback(() => {
    setLocalBaseHex("");
    setLocalBorderHex("");
    setBaseError(null);
    setBorderError(null);
    onClearBase();
  }, [onClearBase]);
  
  const handleUndoBorder = useCallback(() => {
    setLocalBorderHex("");
    setBorderError(null);
    onClearBorder();
  }, [onClearBorder]);
  
  // Effective border for display (user input or AUTO)
  const effectiveBorderDisplay = localBorderHex || autoBorderHex;
  const isBorderAuto = !localBorderHex && !!baseHexNormalized;
  
  return (
    <div className="space-y-3 p-3 bg-muted/40 rounded-md border">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Palette className="h-4 w-4 text-primary" />
          <Label className="text-xs font-medium">Ribbon Styling</Label>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-[240px]">
              <p className="text-xs">
                Set a base HEX color to customize ribbon appearance.
                A "flashy" gradient is auto-derived for visual polish.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      {/* Base HEX */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-[10px]">Base Color (HEX)</Label>
          {hasBaseOverride && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-5 px-1.5 text-[10px]"
                    onClick={handleUndoBase}
                  >
                    <Undo2 className="h-3 w-3 mr-0.5" />
                    Undo
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Clear color override, revert to default</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={localBaseHex}
            onChange={(e) => handleBaseChange(e.target.value)}
            placeholder="#D3AF37"
            className={cn(
              "h-8 text-xs font-mono flex-1",
              baseError && "border-destructive focus-visible:ring-destructive"
            )}
          />
          <ColorSwatch hex={baseHexNormalized || localBaseHex} />
        </div>
        {baseError && (
          <div className="flex items-center gap-1 text-destructive text-[10px]">
            <AlertCircle className="h-3 w-3" />
            {baseError}
          </div>
        )}
      </div>
      
      {/* Border HEX */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Label className="text-[10px]">Border Color (HEX)</Label>
            {isBorderAuto && (
              <Badge variant="outline" className="text-[9px] h-4 px-1">
                AUTO
              </Badge>
            )}
          </div>
          {hasBorderOverride && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-5 px-1.5 text-[10px]"
                    onClick={handleUndoBorder}
                  >
                    <Undo2 className="h-3 w-3 mr-0.5" />
                    Undo
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Clear border override, return to AUTO</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={localBorderHex}
            onChange={(e) => handleBorderChange(e.target.value)}
            placeholder="AUTO (derived from base)"
            disabled={!baseHexNormalized}
            className={cn(
              "h-8 text-xs font-mono flex-1",
              borderError && "border-destructive focus-visible:ring-destructive",
              !baseHexNormalized && "bg-muted cursor-not-allowed"
            )}
          />
          <ColorSwatch 
            hex={effectiveBorderDisplay} 
            isAuto={isBorderAuto}
          />
        </div>
        {borderError && (
          <div className="flex items-center gap-1 text-destructive text-[10px]">
            <AlertCircle className="h-3 w-3" />
            {borderError}
          </div>
        )}
        {!baseHexNormalized && (
          <p className="text-[10px] text-muted-foreground">
            Set Base Color first to enable border customization
          </p>
        )}
      </div>
      
      {/* Gradient Preview */}
      {gradientColors && (
        <GradientPreview colors={gradientColors} />
      )}
      
      {/* Derived values (debug) */}
      {gradientColors && (
        <div className="p-2 bg-background rounded border text-[10px] space-y-0.5">
          <div className="flex gap-2">
            <span className="text-muted-foreground w-20 shrink-0">gradientFrom:</span>
            <code className="font-mono text-foreground truncate">{gradientColors.gradientFrom}</code>
          </div>
          <div className="flex gap-2">
            <span className="text-muted-foreground w-20 shrink-0">gradientTo:</span>
            <code className="font-mono text-foreground truncate">{gradientColors.gradientTo}</code>
          </div>
          <div className="flex gap-2">
            <span className="text-muted-foreground w-20 shrink-0">borderColor:</span>
            <code className="font-mono text-foreground truncate">{gradientColors.borderColor}</code>
          </div>
        </div>
      )}
    </div>
  );
}

export default RibbonStylingSection;
